/* eslint-disable react-refresh/only-export-components -- Application entry point intentionally combines bootstrapping constants and JSX. */

import { StrictMode } from 'react';
import ForgeAccessPage from '@/pages/ForgeAccess/ForgeAccessPage';
import ForgeConnectPage from '@/pages/ForgeAccess/ForgeConnectPage';
import { createRoot } from 'react-dom/client';

// Mantine Styles
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';

// Other styles
import 'flexlayout-react/style/light.css'; // ensure this css is available
import './styles/global.css';
import '@fontsource-variable/inter/wght.css';

import { RedirectToLogin, RequiredAuthProvider } from '@propelauth/react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { queryClient } from '@/services/reactQuery.ts';
import { QueryClientProvider } from '@tanstack/react-query';

import { Button, Center, Container, MantineProvider, Stack, Title } from '@mantine/core';
import theme from '@/theme/theme.ts';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { ApiClientProvider } from '@/context/ApiClientContext/ApiClientProvider.tsx';
import ApiClientContext from '@/context/ApiClientContext/ApiClientContext.tsx';
import axios from 'axios';
import resolver from '@/theme/cssVariablesResolver.ts';
import FullPageSpinner from '@/components/FullPageSpinner.tsx';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { OrgProjectProvider } from '@/context/OrgProjectContext/OrgProjectProvider.tsx';
import AppWithAuth from '@/App.tsx';
import Router from '@/routers/Router.tsx';
import SuiteDraftProvider from '@/context/SuiteDraftContext/SuiteDraftProvider.tsx';
import OrgProjectContext from '@/context/OrgProjectContext/OrgProjectContext.tsx';
import { initializeSentry } from '@/services/sentry.ts';
import PublicPortalPage from '@/pages/PublicPortal/PublicPortalPage.tsx';
import { isPublicPortalPath } from '@/pages/PublicPortal/route.ts';
import { registerChunkLoadRecovery } from '@/utils/chunkLoadRecovery.ts';

const AUTH_URL = import.meta.env.VITE_AUTH_URL;
const E2E_AUTH_BYPASS =
  import.meta.env.MODE === 'e2e' && import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';
const PUBLIC_PORTAL_ROUTE = isPublicPortalPath(
  typeof window === 'undefined' ? undefined : window.location.pathname,
);
const e2eApiClient = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL });

const publicPortalNotFoundElement = (
  <Container>
    <Stack>
      <Center>
        <Title order={1}>This page is not available.</Title>
      </Center>
      <Center>
        <Button fullWidth component="a" href="/" color="blue">
          Go Home
        </Button>
      </Center>
    </Stack>
  </Container>
);

if (!E2E_AUTH_BYPASS) {
  void initializeSentry();
}

registerChunkLoadRecovery();

const root = PUBLIC_PORTAL_ROUTE ? (
  <StrictMode>
    <MantineProvider theme={theme} cssVariablesResolver={resolver}>
      <Notifications />
      <ModalsProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              <Route
                path="/portals/:portalIdentifier/runs/:publicRunToken"
                element={<PublicPortalPage />}
              />
              <Route path="/portals/:portalIdentifier" element={<PublicPortalPage />} />
              <Route path="*" element={publicPortalNotFoundElement} />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </StrictMode>
) : E2E_AUTH_BYPASS ? (
  <StrictMode>
    <MantineProvider theme={theme} cssVariablesResolver={resolver}>
      <Notifications />
      <ModalsProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ApiClientContext.Provider value={e2eApiClient}>
              <Routes>
                <Route path="/forge/connect" element={<ForgeConnectPage />} />
                <Route path="/forge/access" element={<ForgeAccessPage />} />
                <Route path="/projects/:projectId/forge-access" element={<ForgeAccessPage />} />
                <Route
                  path="*"
                  element={
                    <OrgProjectContext.Provider
                      value={{
                        orgId: 'e2e-org',
                        setOrgId: () => undefined,
                        projectId: 'e2e-project',
                        setProjectId: () => undefined,
                        loading: false,
                        error: null,
                      }}
                    >
                      <SuiteDraftProvider identity="e2e-user">
                        <Router />
                      </SuiteDraftProvider>
                    </OrgProjectContext.Provider>
                  }
                />
              </Routes>
            </ApiClientContext.Provider>
          </BrowserRouter>
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </StrictMode>
) : (
  <StrictMode>
    <MantineProvider theme={theme} cssVariablesResolver={resolver}>
      <Notifications />
      <ModalsProvider>
        <QueryClientProvider client={queryClient}>
          <RequiredAuthProvider
            authUrl={AUTH_URL}
            displayWhileLoading={<FullPageSpinner />}
            displayIfLoggedOut={<RedirectToLogin postLoginRedirectUrl={window.location.href} />}
          >
            <BrowserRouter>
              <ApiClientProvider>
                <Routes>
                  <Route path="/forge/connect" element={<ForgeConnectPage />} />
                  <Route path="/forge/access" element={<ForgeAccessPage />} />
                  <Route path="/projects/:projectId/forge-access" element={<ForgeAccessPage />} />
                  <Route
                    path="*"
                    element={
                      <OrgProjectProvider>
                        <AppWithAuth />
                      </OrgProjectProvider>
                    }
                  />
                </Routes>
              </ApiClientProvider>
            </BrowserRouter>
          </RequiredAuthProvider>
          {import.meta.env.VITE_ENV === 'development' && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </StrictMode>
);

createRoot(document.getElementById('root')!).render(root);
